import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useAuth } from "@/lib/auth";
import { BILLING_ADDRESS_DESCRIPTION, type PaymentFormValues } from "@/lib/checkout-form";
import type { PaymentSubmission } from "@/lib/payment-flow";
import { AddressFields } from "@/components/AddressFields";
import { PaymentFields } from "@/components/PaymentFields";
import { ErrorMessage } from "@/components/ErrorMessage";

/**
 * The two parts every roster-initiated checkout page shares (Plan 2, Task 10):
 * the billing-address + card block, and the error + submit-button block. Both
 * render inside the page's own FormProvider and read the form through context,
 * whose data type is a superset of PaymentFormValues.
 */

function isBlank(a?: { street: string; city: string; state: string; zip: string }): boolean {
  return !a || (!a.street && !a.city && !a.state && !a.zip);
}

/** Billing address + card. The billing address is prefilled from the vendor
 *  record exactly as the public checkout does it: only into a block still empty,
 *  so what the vendor typed always wins. It keeps its card-statement meaning
 *  (decision 2026-07-30). */
export function CheckoutPaymentSection(): React.ReactElement {
  const { vendor } = useAuth();
  const { getValues, setValue } = useFormContext<PaymentFormValues>();

  useEffect(() => {
    if (!vendor) return;
    if (isBlank(getValues("billingAddress"))) {
      setValue("billingAddress", vendor.billingAddress);
    }
  }, [vendor, getValues, setValue]);

  return (
    <>
      <section>
        <AddressFields
          prefix="billingAddress"
          label="Billing Address"
          description={BILLING_ADDRESS_DESCRIPTION}
        />
      </section>
      <section>
        <PaymentFields />
      </section>
    </>
  );
}

interface CheckoutSubmitSectionProps {
  payment: Pick<PaymentSubmission<never>, "isSubmitting" | "isHeld" | "submitError">;
  /** The button's text with the total, or undefined while the amount is not
   *  known: there is no fallback price, so the order cannot be placed yet. */
  label: string | undefined;
  /** The configured price(s) failed to load. */
  priceFailed?: boolean;
}

export function CheckoutSubmitSection({
  payment,
  label,
  priceFailed = false,
}: CheckoutSubmitSectionProps): React.ReactElement {
  const {
    formState: { isSubmitting },
  } = useFormContext<PaymentFormValues>();
  const busy = isSubmitting || payment.isSubmitting;

  return (
    <>
      {payment.submitError && <ErrorMessage message={payment.submitError} />}

      {priceFailed && (
        <ErrorMessage message="We could not load the current price, so the order cannot be placed yet. Please reload the page to try again." />
      )}

      <button
        type="submit"
        disabled={busy || payment.isHeld || label === undefined}
        className="w-full rounded-md bg-blue-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? "Processing..." : (label ?? "Place Order")}
      </button>
    </>
  );
}
